<script setup lang="ts">
import { useRoute } from 'vue-router';
import { onMounted, ref, type Ref } from 'vue';
import { User } from '@/models/User';
import KpModal from '@kp/geofy-vuejs-lib/tg-system/components/KpModal.vue';
import { instance as api } from '@/helpers/api';
import { errToStr } from '@/helpers/tools';
import KpDropdownMultiSelect from '@kp/geofy-vuejs-lib/tg-system/components/KpDropdownMultiSelect.vue';
import { useStelleCollection } from '@/composables/useStelleCollection';
import { type UseCollectionOptions } from '@/composables/useDataTableProps';

const route = useRoute();
const user: Ref<User | null> = ref(null);
const loading: Ref<boolean> = ref(false);

const showPwDlg = defineModel({ default: false });
const newPwd: Ref<string | null> = ref(null);
const pwdError: Ref<string> = ref('');

onMounted(async () => {
    await loadUser(Number(route.params.id));
    query();
});

const { models, query } = useStelleCollection({
    autoload: true,
    pageSize: 0
} as UseCollectionOptions);

async function loadUser(id: number) {
    if (user.value && Number(route.params.id) === Number(id)) {
        return;
    }
    loading.value = true;
    try {
        user.value = null;
        user.value = await User.loadById(Number(id));
    } catch (e) {
        console.error(e);
    } finally {
        loading.value = false;
    }
}

function mutateRecord(field: string, value: unknown) {
    user?.value?.set(field, value);
}

async function mutateStelleIds(value: number[] | null) {
    if (!user.value || user.value.id === null || loading.value) {
        return;
    }
    loading.value = true;
    try {
        await api().post('/user/setStellen', {
            user_id: user.value.id,
            stelle_ids: value
        });
    } catch (e) {
        console.error(e);
    } finally {
        loading.value = false;
        //reload user?: loadUser(user.value.id);
    }
}

async function onSave() {
    loading.value = true;
    try {
        if (user?.value) {
            await user?.value?.save();
        }
    } finally {
        loading.value = false;
    }
}

async function onSetPwdClick() {
    loading.value = true;
    try {
        await api().post('/auth/changePasswordAdmin', {
            new_password: newPwd.value,
            user_id: user?.value?.id
        });
        pwdError.value = '';
        showPwDlg.value = false;
    } catch (e) {
        pwdError.value = errToStr(e);
    } finally {
        loading.value = false;
    }
}
</script>
<template>
    <div>
        <RouterLink :to="{ name: 'admin-users' }">
            <ktg-button appearance="tertiary" size="small" leading-icon="arrow-left"> {{ $t('back') }}</ktg-button>
        </RouterLink>
    </div>
    <form @submit.prevent="onSave">
        <div class="user_grid mt-0_25rem" role="table" style="width: 500px">
            <div>
                <ktg-field>
                    <ktg-label for="username">{{ $t('username') }}</ktg-label>

                    <ktg-text-input
                        :value="user?.username"
                        input-id="username"
                        type="text"
                        autofocus
                        @change="mutateRecord('username', $event.target.value)"
                    >
                    </ktg-text-input>
                </ktg-field>
            </div>
            <div>
                <ktg-field>
                    <ktg-label for="email">{{ $t('email') }}</ktg-label>

                    <ktg-text-input
                        :value="user?.email"
                        input-id="email"
                        type="text"
                        autofocus
                        @change="mutateRecord('email', $event.target.value)"
                    >
                    </ktg-text-input>
                </ktg-field>
            </div>
            <div class="colspan-2">
                <ktg-field>
                    <ktg-label for="stelle_ids">{{ $t('stellen') }} </ktg-label>
                    <KpDropdownMultiSelect
                        class="w-full"
                        :options="models"
                        :model-value="user?.stelle_ids"
                        value-field="id"
                        display-field="organisation_name"
                        input-id="stelle_ids"
                        name="stellen"
                        :selected-text="`{selectedCount} ${$t('entries_selected')}`"
                        :no-selection-text="$t('please_select')"
                        with-pills
                        @update:model-value="mutateStelleIds($event as number[] | null)"
                    ></KpDropdownMultiSelect>
                </ktg-field>
            </div>
            <div>
                <ktg-field>
                    <ktg-label for="vorname">{{ $t('vorname') }}</ktg-label>

                    <ktg-text-input
                        :value="user?.vorname"
                        input-id="vorname"
                        type="text"
                        autofocus
                        @change="mutateRecord('vorname', $event.target.value)"
                    >
                    </ktg-text-input>
                </ktg-field>
            </div>
            <div>
                <ktg-field>
                    <ktg-label for="nachname">{{ $t('nachname') }}</ktg-label>

                    <ktg-text-input
                        :value="user?.nachname"
                        input-id="nachname"
                        type="text"
                        autofocus
                        @change="mutateRecord('nachname', $event.target.value)"
                    >
                    </ktg-text-input>
                </ktg-field>
            </div>
            <div>
                <ktg-field>
                    <ktg-label for="street_address">{{ $t('street_address') }}</ktg-label>

                    <ktg-text-input
                        :value="user?.street_address"
                        input-id="street_address"
                        type="text"
                        autofocus
                        @change="mutateRecord('street_address', $event.target.value)"
                    >
                    </ktg-text-input>
                </ktg-field>
            </div>
            <div>
                <ktg-field>
                    <ktg-label for="strassennummer">{{ $t('strassennummer') }}</ktg-label>

                    <ktg-text-input
                        :value="user?.strassennummer"
                        input-id="strassennummer"
                        type="text"
                        autofocus
                        @change="mutateRecord('strassennummer', $event.target.value)"
                    >
                    </ktg-text-input>
                </ktg-field>
            </div>
            <div>
                <ktg-field>
                    <ktg-label for="postal_code">{{ $t('plz') }}</ktg-label>

                    <ktg-text-input
                        :value="user?.postal_code"
                        input-id="postal_code"
                        type="text"
                        autofocus
                        @change="mutateRecord('postal_code', $event.target.value)"
                    >
                    </ktg-text-input>
                </ktg-field>
            </div>
            <div>
                <ktg-field>
                    <ktg-label for="locality">{{ $t('ort') }}</ktg-label>

                    <ktg-text-input
                        :value="user?.locality"
                        input-id="locality"
                        type="text"
                        autofocus
                        @change="mutateRecord('locality', $event.target.value)"
                    >
                    </ktg-text-input>
                </ktg-field>
            </div>
            <div>
                <ktg-field>
                    <ktg-label for="country">{{ $t('country') }}</ktg-label>

                    <ktg-text-input
                        :value="user?.country"
                        input-id="country"
                        type="text"
                        autofocus
                        @change="mutateRecord('country', $event.target.value)"
                    >
                    </ktg-text-input>
                </ktg-field>
            </div>
            <div></div>
            <div>
                <ktg-field>
                    <ktg-label for="titel">{{ $t('titel') }}</ktg-label>

                    <ktg-text-input
                        :value="user?.titel"
                        input-id="titel"
                        type="text"
                        autofocus
                        @change="mutateRecord('titel', $event.target.value)"
                    >
                    </ktg-text-input>
                </ktg-field>
            </div>
            <div>
                <ktg-field>
                    <ktg-label for="salutation">{{ $t('anrede') }}</ktg-label>

                    <ktg-text-input
                        :value="user?.salutation"
                        input-id="salutation"
                        type="text"
                        autofocus
                        @change="mutateRecord('salutation', $event.target.value)"
                    >
                    </ktg-text-input>
                </ktg-field>
            </div>
            <div>
                <ktg-field>
                    <ktg-label for="mobile_number">{{ $t('mobile') }}</ktg-label>

                    <ktg-text-input
                        :value="user?.mobile_number"
                        input-id="mobile_number"
                        type="text"
                        autofocus
                        @change="mutateRecord('mobile_number', $event.target.value)"
                    >
                    </ktg-text-input>
                </ktg-field>
            </div>
            <div></div>
            <div>
                <ktg-labelled-choice>
                    <ktg-checkbox
                        :checked="user?.ist_admin === true"
                        input-id="ist_admin"
                        autofocus
                        @change="mutateRecord('ist_admin', $event.target?.checked)"
                    >
                    </ktg-checkbox>
                    <ktg-label for="ist_admin">{{ $t('admin') }}</ktg-label>
                </ktg-labelled-choice>
            </div>
            <div>
                <ktg-labelled-choice>
                    <ktg-checkbox
                        :checked="user?.api_user === true"
                        input-id="api_user"
                        autofocus
                        @change="mutateRecord('api_user', $event.target?.checked)"
                    >
                    </ktg-checkbox>
                    <ktg-label for="api_user">{{ $t('api_user') }}</ktg-label>
                </ktg-labelled-choice>
            </div>
        </div>

        <div class="mt-1rem mb-1rem">
            <ktg-button class="confirm-button" size="small" :disabled="loading" @click="showPwDlg = true">
                {{ $t('passwort_setzen') }}
            </ktg-button>
        </div>

        <div class="mt-1rem mb-1rem">
            <RouterLink :to="{ name: 'admin-users' }">
                <ktg-button class="cancel-button" appearance="secondary" size="small" :disabled="loading">
                    {{ $t('cancel') }}
                </ktg-button>
            </RouterLink>
            <ktg-button class="ml-1rem confirm-button" size="small" :disabled="loading" type="submit">
                {{ $t('save') }}
            </ktg-button>
        </div>

        <KpModal v-model="showPwDlg">
            <ktg-field>
                <ktg-label for="pwd">{{ $t('new_password') }}</ktg-label>
                <ktg-text-input
                    type="password"
                    input-id="pwd"
                    class="w-full"
                    error-id="pwd-error"
                    :value="newPwd"
                    :placeholder="$t('new_password')"
                    @input="newPwd = $event.target.value"
                    :maxlength="1000"
                    rows="10"
                ></ktg-text-input>
            </ktg-field>
            <ktg-field-error id="pwd-error" v-if="pwdError !== ''">{{ pwdError }}</ktg-field-error>
            <ktg-modal-buttons>
                <ktg-button appearance="secondary" :disabled="loading" @click="showPwDlg = false">
                    {{ $t('cancel') }}
                </ktg-button>
                <ktg-button appearance="primary" :loading="loading" :disabled="loading" @click="onSetPwdClick">
                    {{ $t('passwort_setzen') }}
                </ktg-button>
            </ktg-modal-buttons>
        </KpModal>
    </form>
</template>

<style scoped lang="scss">
.user_grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    .header {
        border-bottom: 2px solid var(--ktg-color-primary-01);
    }
    .colspan-2 {
        grid-column: span 2;
    }
}
</style>
